import { useEffect, useState } from "react";
import { CheckCircle2, FileText, Sparkles, BookOpen } from "lucide-react";

interface Step {
  key: string;
  label: string;
  hint: string;
  icon: typeof FileText;
  /** ms to spend on this step before advancing. Last step stays active. */
  durationMs: number;
}

const STEPS: Step[] = [
  {
    key: "extract",
    label: "Reading the document",
    hint: "Extracting text from the PDF",
    icon: FileText,
    durationMs: 1800,
  },
  {
    key: "rules",
    label: "Loading active rules",
    hint: "Pulling this org's validation rules",
    icon: BookOpen,
    durationMs: 1200,
  },
  {
    key: "analyze",
    label: "Analyzing with AI",
    hint: "The model is thinking — this is the slow bit",
    icon: Sparkles,
    durationMs: Infinity,
  },
];

const TIPS = [
  "Each rule is checked against the document text in one shot.",
  "Validation results are advisory — you can always upload anyway.",
  "Long PDFs are truncated to the first ~50K characters.",
  "The model can mark rules as ✓ passed, ✗ failed, or ? unaddressed.",
];

interface Props {
  fileName?: string;
  fileSize?: string;
}

export function ValidationProgress({ fileName, fileSize }: Props) {
  const [current, setCurrent] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [tipIdx, setTipIdx] = useState(0);

  useEffect(() => {
    const start = Date.now();
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 250);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (current >= STEPS.length - 1) return;
    const t = setTimeout(
      () => setCurrent((c) => c + 1),
      STEPS[current].durationMs,
    );
    return () => clearTimeout(t);
  }, [current]);

  useEffect(() => {
    const t = setInterval(() => {
      setTipIdx((i) => (i + 1) % TIPS.length);
    }, 4500);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="space-y-5 py-2">
      {/* File context */}
      {fileName && (
        <div className="flex items-center justify-center gap-2 text-xs d">
          <FileText className="size-3.5" />
          <span className="font-medium text-foreground">{fileName}</span>
          {fileSize && <span>· {fileSize}</span>}
        </div>
      )}

      {/* Step list */}
      <ol className="space-y-2.5">
        {STEPS.map((step, i) => {
          const status: "done" | "active" | "pending" =
            i < current ? "done" : i === current ? "active" : "pending";
          const Icon = step.icon;
          return (
            <li
              key={step.key}
              className={`flex items-start gap-3 px-3 py-2.5 rounded-lg border transition-colors ${
                status === "active"
                  ? "border-indigo-200 dark:border-indigo-500/30 bg-indigo-50/50 dark:bg-indigo-500/[0.06]"
                  : status === "done"
                    ? "border-emerald-200/60 dark:border-emerald-500/20 bg-emerald-50/40 dark:bg-emerald-500/[0.04]"
                    : "border-[#e2e8f0] dark:border-white/[0.06] bg-[#f8fafc] dark:bg-white/[0.02]"
              }`}
            >
              {/* Status icon */}
              <div className="flex-shrink-0 mt-0.5">
                {status === "done" ? (
                  <CheckCircle2 className="size-5 text-emerald-500" />
                ) : status === "active" ? (
                  <div className="relative size-5">
                    <span className="absolute inset-0 rounded-full bg-indigo-500/30 animate-ping" />
                    <span className="absolute inset-1 rounded-full bg-indigo-500 flex items-center justify-center">
                      <Icon className="size-2.5 text-white" />
                    </span>
                  </div>
                ) : (
                  <div className="size-5 rounded-full border-2 border-[#cbd5e1] dark:border-white/15 flex items-center justify-center">
                    <Icon className="size-2.5 text-[#cbd5e1] dark:text-white/30" />
                  </div>
                )}
              </div>

              {/* Label + hint */}
              <div className="min-w-0 flex-1">
                <div
                  className={`text-sm font-medium ${
                    status === "pending" ? "d" : "text-foreground"
                  }`}
                >
                  {step.label}
                  {status === "active" && <AnimatedEllipsis />}
                </div>
                <div className="text-xs d mt-0.5">{step.hint}</div>
              </div>

              {/* Active step gets elapsed timer */}
              {status === "active" && (
                <div className="flex-shrink-0 text-xs font-mono tabular-nums text-indigo-600 dark:text-indigo-400 mt-0.5">
                  {formatElapsed(elapsed)}
                </div>
              )}
            </li>
          );
        })}
      </ol>

      {/* Rotating tip */}
      <div className="text-xs text-center d italic min-h-[1.25rem] transition-opacity">
        {TIPS[tipIdx]}
      </div>
    </div>
  );
}

function formatElapsed(s: number): string {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

function AnimatedEllipsis() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => (x + 1) % 4), 400);
    return () => clearInterval(t);
  }, []);
  const dots = ".".repeat(tick);
  return (
    <span className="inline-block w-4 text-left text-indigo-500 dark:text-indigo-400">
      {dots}
    </span>
  );
}
